import { useState, useMemo, useCallback, type ReactNode } from "react";
import {
  useTellescopeInfiniteQuery,
  type UseTellescopeInfiniteQueryOptions,
  type ParamsForMethod,
} from "@tellescope/tanstack-query";
import { Combobox, ComboboxOption, ComboboxOptionRenderProps, DeletableBadge } from "@tellescope/ui";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { formatName } from "@tellescope/utils";
import { i18n } from "@tellescope/i18n";
import { DefaultEnduserOption, type DefaultRendererVariant } from "./DefaultEnduserOption";
import type { Enduser } from "../types";

/**
 * Props for the EnduserSearchCombobox component
 */
export interface EnduserSearchComboboxProps {
  // ===== Selection (Controlled State) =====
  /** Current selected enduser(s) - MUST be controlled externally */
  value: Enduser[];

  /** Callback when selection changes */
  onChange: (value: Enduser[]) => void;

  /** Enable multi-select mode */
  multiple?: boolean;

  // ===== Search Configuration =====
  /** Debounce delay for API search in milliseconds (default: 500) */
  debounceMs?: number;

  // ===== Freeform Ids =====
  /**
   * Enduser ids that are selected but have no loaded enduser record — typically ids the search can't
   * surface, entered by hand. Rendered alongside `value` as their bare id. Requires `onFreeformIdsChange`
   * to be selectable.
   */
  freeformIds?: string[];

  /**
   * When provided, the current search text is offered as a selectable option so the user can commit an
   * id the search doesn't surface (e.g. a bare enduser ID). Picking or clearing one reports the new
   * freeform id list here rather than through `onChange`, which only ever carries real enduser records.
   */
  onFreeformIdsChange?: (ids: string[]) => void;

  // ===== Query Configuration =====
  /**
   * Optional additional TanStack Query options.
   * The `model`, `method`, `request.search`, and `request.limit` are managed internally.
   * You can provide additional request params (e.g., filters), or override `enabled`, `staleTime`, etc.
   */
  queryOptions?: Partial<
    Omit<
      UseTellescopeInfiniteQueryOptions<"endusers", "getSome", string | undefined, Error>,
      "model" | "method" | "getNextPageParam" | "initialPageParam" | "paginateRequest" | "select"
    >
  > & {
    /** Additional request parameters to merge with the search query */
    requestOverrides?: Partial<ParamsForMethod<"endusers", "getSome">>;
  };

  // ===== UI Customization =====
  /**
   * Default option renderer variant to use.
   * - "default": Shows only enduser name
   * - "phone": Shows enduser name with phone number
   * - "email": Shows enduser name with email
   * Can be overridden by providing formatOption prop.
   */
  defaultRendererVariant?: DefaultRendererVariant;

  /** Custom option renderer - receives enduser data via metadata */
  formatOption?: (props: ComboboxOptionRenderProps<Enduser>) => ReactNode;

  /** Custom selected value formatter */
  formatSelectedValue?: (value: Enduser[]) => ReactNode;

  /** Placeholder text for trigger button (default: "Select enduser...") */
  placeholder?: string;

  /** Placeholder text for search input (default: "Search by name, email, or phone...") */
  searchPlaceholder?: string;

  /** Text shown while loading results (default: "Searching...") */
  loadingText?: string;

  /** Text shown when no results found (default: "No endusers found") */
  emptyText?: string;

  /** Show clear button (default: true) */
  clearable?: boolean;

  /** CSS class for trigger button */
  className?: string;

  /** CSS class for popover content */
  popoverClassName?: string;

  /** Accessibility label */
  "aria-label"?: string;

  /** Custom filter function for client-side filtering of options */
  customFilter?: (searchTerm: string, options: ComboboxOption<Enduser>[]) => ComboboxOption<Enduser>[];

  /** When true, display only "{count} selected" instead of listing names */
  onlyDisplayCount?: boolean;

  /** When true, display selected endusers as removable badges */
  showAsBadges?: boolean;
}

/**
 * Reusable enduser search combobox component with debounced API search.
 *
 * This component wraps the base Combobox with enduser-specific functionality:
 * - Automatic TanStack Query integration with debounced search
 * - Controlled enduser selection (single or multi-select)
 * - Three default option renderer variants (default, phone, email)
 * - Optional freeform ids, for committing an enduser id the search can't surface
 * - Customizable query options and rendering
 *
 * @example
 * // Simple single-select
 * const [selectedEndusers, setSelectedEndusers] = useState<Enduser[]>([]);
 * <EnduserSearchCombobox
 *   value={selectedEndusers}
 *   onChange={setSelectedEndusers}
 * />
 *
 * @example
 * // Multi-select with phone display
 * const [selectedEndusers, setSelectedEndusers] = useState<Enduser[]>([]);
 * <EnduserSearchCombobox
 *   multiple
 *   value={selectedEndusers}
 *   onChange={setSelectedEndusers}
 *   defaultRendererVariant="phone"
 * />
 *
 * @example
 * // With filters and custom options
 * <EnduserSearchCombobox
 *   value={selectedEndusers}
 *   onChange={setSelectedEndusers}
 *   queryOptions={{
 *     requestOverrides: {
 *       filter: { tags: ["vip"] }
 *     },
 *     staleTime: 30000
 *   }}
 * />
 *
 * @example
 * // Custom rendering
 * <EnduserSearchCombobox
 *   value={selectedEndusers}
 *   onChange={setSelectedEndusers}
 *   formatOption={(props) => <CustomEnduserItem {...props} />}
 * />
 *
 * @example
 * // Let the user commit a bare enduser id the search can't surface
 * <EnduserSearchCombobox
 *   value={selectedEndusers}
 *   onChange={setSelectedEndusers}
 *   freeformIds={freeformIds}
 *   onFreeformIdsChange={setFreeformIds}
 * />
 */
export const EnduserSearchCombobox = ({
  value,
  onChange,
  multiple,
  debounceMs = 500,
  freeformIds,
  onFreeformIdsChange,
  queryOptions,
  defaultRendererVariant = "default",
  formatOption,
  formatSelectedValue,
  placeholder = i18n.t("Select enduser...", { ns: "tickets" }),
  searchPlaceholder = i18n.t("Search by name, email, or phone...", { ns: "tickets" }),
  loadingText = i18n.t("Searching...", { ns: "tickets" }),
  emptyText = i18n.t("No endusers found", { ns: "tickets" }),
  clearable = true,
  className,
  popoverClassName,
  "aria-label": ariaLabel,
  customFilter,
  onlyDisplayCount,
  showAsBadges,
}: EnduserSearchComboboxProps) => {
  // Internal search state with debouncing
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, debounceMs);

  // Query endusers with debounced search
  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 20,
      search: debouncedSearchQuery ? { query: debouncedSearchQuery } : undefined,
      ...queryOptions?.requestOverrides,
    },
    select: (data) => data.pages.flat(),
    ...queryOptions,
  });

  // Map endusers to combobox options, plus the raw search text when freeform ids are enabled
  const comboboxOptions: ComboboxOption<Enduser>[] = useMemo(() => {
    const matches =
      endusersQuery.data?.map((enduser) => ({
        value: enduser.id,
        label: formatName(enduser),
        metadata: { enduser },
      })) ?? [];

    const trimmed = searchQuery.trim();
    if (onFreeformIdsChange && trimmed && !matches.some((option) => option.value === trimmed)) {
      // No metadata: a freeform option is an id with no loaded record behind it.
      return [...matches, { value: trimmed, label: i18n.t('Use "{{value}}"', { value: trimmed, ns: "tickets" }) }];
    }

    return matches;
  }, [endusersQuery.data, onFreeformIdsChange, searchQuery]);

  // Convert external value to combobox format (always array for internal Combobox)
  const comboboxValue: ComboboxOption<Enduser>[] = useMemo(
    () => [
      ...value.map((enduser) => ({
        value: enduser.id,
        label: formatName(enduser) || enduser.id,
        metadata: { enduser },
      })),
      ...(freeformIds ?? []).map((id) => ({ value: id, label: id })),
    ],
    [value, freeformIds]
  );

  // Handle selection changes - convert array back to single value if needed
  const handleChange = useCallback(
    (options: ComboboxOption<Enduser>[]) => {
      const endusers = options.reduce<Enduser[]>((acc, option) => {
        const enduser = option.metadata?.enduser;
        return enduser ? [...acc, enduser] : acc;
      }, []);
      // Options with no enduser record behind them are freeform ids.
      const pickedFreeformIds = options.reduce<string[]>(
        (acc, option) => (option.metadata?.enduser ? acc : [...acc, option.value]),
        []
      );

      if (multiple) {
        onChange(endusers);
        onFreeformIdsChange?.(pickedFreeformIds);
        return;
      }

      // Single-select: a click on the already-selected option deselects it, so keep only a newly added one.
      const currentIds = [...value.map(({ id }) => id), ...(freeformIds ?? [])];
      const addedEndusers = endusers.filter(({ id }) => !currentIds.includes(id));
      const addedFreeformIds = pickedFreeformIds.filter((id) => !currentIds.includes(id));

      if (addedEndusers.length > 0) {
        onChange(addedEndusers.slice(0, 1));
        onFreeformIdsChange?.([]);
        return;
      }

      onChange([]);
      onFreeformIdsChange?.(addedFreeformIds.slice(0, 1));
    },
    [multiple, onChange, onFreeformIdsChange, value, freeformIds]
  );

  // Default option renderer
  const defaultFormatOption = useMemo(
    () => (props: ComboboxOptionRenderProps<Enduser>) => {
      const enduser = props.option.metadata?.enduser;
      // A freeform option has no enduser record behind it, so there is nothing to lay out — but it
      // still needs its label rendered, or the option is present and selectable yet invisible.
      if (!enduser) return <div className="font-medium text-sm truncate">{props.option.label}</div>;
      return <DefaultEnduserOption enduser={enduser} isSelected={props.isSelected} variant={defaultRendererVariant} />;
    },
    [defaultRendererVariant]
  );

  // Wrap custom formatSelectedValue to convert ComboboxOption to Enduser
  const wrappedFormatSelectedValue = useMemo(() => {
    // Custom formatter takes priority
    if (formatSelectedValue) {
      return (val: ComboboxOption<Enduser>[]) => {
        const endusers = val.reduce<Enduser[]>((acc, option) => {
          const enduser = option.metadata?.enduser;
          return enduser ? [...acc, enduser] : acc;
        }, []);
        return formatSelectedValue(endusers);
      };
    }

    if (onlyDisplayCount) {
      return (val: ComboboxOption<Enduser>[]) => {
        if (val.length === 0) return null;
        return <span>{i18n.t("{{amount}} selected", { amount: val.length, ns: "tickets" })}</span>;
      };
    }

    return undefined;
  }, [formatSelectedValue, onlyDisplayCount]);

  return (
    <div className="space-y-2">
      <Combobox
        // Selection
        value={comboboxValue}
        onChange={handleChange}
        multiple
        // Options
        options={comboboxOptions}
        alwaysFilter={false}
        customFilter={customFilter}
        // Search
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchPlaceholder={searchPlaceholder}
        // Rendering
        formatOption={formatOption ?? defaultFormatOption}
        formatSelectedValue={wrappedFormatSelectedValue}
        // Loading states
        isLoading={endusersQuery.isLoading || searchQuery !== debouncedSearchQuery}
        loadingText={loadingText}
        emptyText={emptyText}
        // UI
        placeholder={placeholder}
        clearable={clearable}
        className={className}
        popoverClassName={popoverClassName}
        aria-label={ariaLabel}
      />
      {showAsBadges && value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((enduser) => (
            <DeletableBadge key={enduser.id} onDelete={() => onChange(value.filter((e) => e.id !== enduser.id))}>
              {formatName(enduser)}
            </DeletableBadge>
          ))}
        </div>
      )}
    </div>
  );
};
